import { useEffect, useRef, useState } from "react";
import {
  FiBox,
  FiCrosshair,
  FiSun,
  FiCamera,
  FiEye,
  FiEyeOff,
  FiChevronRight,
  FiChevronDown,
  FiGlobe,
} from "react-icons/fi";
import {
  TbSquare,
  TbCube,
  TbCircle,
  TbCone,
  TbCylinder,
  TbAxisX,
  TbSun,
  TbBulb,
  TbCamera,
  TbFileImport,
} from "react-icons/tb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/dropdown-menu";
import { childrenOf, isDescendantOf, WORLD_NODE_ID } from "./nodeTree";
import {
  tabsForNodeType,
  WORLD_TABS,
  TRANSFORM_TAB,
  EXTRA_TAB_COLUMNS,
} from "./propertiesTabs";

const NODE_ICON = {
  mesh: FiBox,
  null: FiCrosshair,
  light: FiSun,
  camera: FiCamera,
};

const DRAG_MIME = "application/x-scene3d-node";

const GLB_ACCEPT = ".glb,model/gltf-binary";

const PRIMITIVES = [
  { geometry: "plane", label: "Plane", Icon: TbSquare },
  { geometry: "cube", label: "Cube", Icon: TbCube },
  { geometry: "sphere", label: "Sphere", Icon: TbCircle },
  { geometry: "cone", label: "Cone", Icon: TbCone },
  { geometry: "cylinder", label: "Cylinder", Icon: TbCylinder },
];

// A light's type can't change after creation (see PropertiesPanel.jsx — its
// "Light Settings" tab no longer offers a type selector), so it has to be
// chosen right here, at the point the node is born.
const LIGHT_TYPES = [
  { lightType: "point", label: "Point Light", Icon: TbBulb },
  { lightType: "directional", label: "Directional Light", Icon: TbSun },
];

// Toolbar for adding nodes, above the tree itself — grouped under a single
// "Geometry" dropdown (primitives + Null + GLB import) since those all drop
// a mesh-shaped node into the hierarchy, while Light and Camera get their own
// buttons since they're reached for independently and far more often.
function AddNodeToolbar({
  onAddMesh,
  onImportModel,
  importingModel,
  onAddNull,
  onAddLight,
  onAddCamera,
}) {
  const modelFileInputRef = useRef(null);
  const btn =
    "flex items-center gap-1 px-2 py-1 rounded-sm text-xs text-secondary-foreground hover:bg-secondary/60 hover:text-white cursor-pointer";

  return (
    <div
      id="Scene3DHierarchyPanel__toolbar"
      className="flex items-center gap-1 p-1 border-b border-secondary/40 flex-shrink-0">
      <input
        id="Scene3DHierarchyPanel__modelFileInput"
        ref={modelFileInputRef}
        type="file"
        accept={GLB_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = ""; // so picking the same file twice still fires onChange
          if (file) onImportModel(file);
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            id="Scene3DHierarchyPanel__geometryTrigger"
            type="button"
            className={btn}
            title="Add Geometry">
            <TbCube className="w-4 h-4" />
            <span>Geometry</span>
            <FiChevronDown className="w-3 h-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {PRIMITIVES.map(({ geometry, label, Icon }) => (
            <DropdownMenuItem
              id={`Scene3DHierarchyPanel__geometryOption-${geometry}`}
              key={geometry}
              className="text-xs"
              onClick={() => onAddMesh(geometry)}>
              <Icon className="!w-4 !h-4" />
              {label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            id="Scene3DHierarchyPanel__addNullOption"
            className="text-xs"
            onClick={onAddNull}>
            <TbAxisX className="w-3 h-3" />
            Null
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            id="Scene3DHierarchyPanel__importModelOption"
            disabled={importingModel}
            className="text-xs"
            onClick={() => modelFileInputRef.current?.click()}>
            <TbFileImport className="w-4 h-4" />
            {importingModel ? "Importing..." : "Import GLB..."}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            id="Scene3DHierarchyPanel__addLightTrigger"
            type="button"
            className={btn}
            title="Add Light">
            <TbSun className="w-4 h-4" />
            <span>Light</span>
            <FiChevronDown className="w-3 h-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {LIGHT_TYPES.map(({ lightType, label, Icon }) => (
            <DropdownMenuItem
              id={`Scene3DHierarchyPanel__lightOption-${lightType}`}
              key={lightType}
              className="text-xs"
              onClick={() => onAddLight(lightType)}>
              <Icon className="!w-4 !h-4" />
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        id="Scene3DHierarchyPanel__addCameraTrigger"
        type="button"
        onClick={onAddCamera}
        className={btn}
        title="Add Camera">
        <TbCamera className="w-4 h-4" />
        Camera
      </button>
    </div>
  );
}

// Drives the yellow dot on a row's AI tab icon — a hint that this element
// already carries AI generation input, visible without opening the tab.
const hasAiContent = (node) =>
  !!(node.aiDescription?.trim() || node.aiReferenceImages?.length);

// A row's vertical thirds decide the drop meaning: top/bottom thirds reorder
// as a sibling before/after the row, the middle third reparents as a child —
// same convention as VS Code's / Blender's outliner drag-drop.
const dropPositionFor = (event, rect) => {
  const ratio = (event.clientY - rect.top) / rect.height;
  if (ratio < 0.25) return "before";
  if (ratio > 0.75) return "after";
  return "inside";
};

// Fixed row for scene-wide settings (ambient light, world AI description) —
// not a real node, so it skips everything node-specific: no drag/drop,
// rename, visibility toggle, or expand/collapse, just its two fixed tabs.
function WorldRow({ selected, activeTab, onSelect, onSelectTab }) {
  // Falls back to the first World tab when `activeTab` is left over from a
  // previously selected node (e.g. "transform") — mirrors the same fallback
  // PropertiesPanel applies when rendering World's content.
  const effectiveTab = WORLD_TABS.some((t) => t.id === activeTab)
    ? activeTab
    : WORLD_TABS[0].id;
  return (
    <div
      id="Scene3DHierarchy__worldRow"
      onClick={onSelect}
      className={`flex items-center gap-1 px-2 py-1 cursor-pointer text-xs select-none
        ${selected ? "bg-secondary/60 text-primary" : "hover:bg-secondary/30"}`}>
      <FiGlobe className="w-3.5 h-3.5 shrink-0 opacity-70" />
      <span className="flex-1 truncate">World</span>
      <div className="flex gap-1">
        {WORLD_TABS.map(({ id, label, icon: TabIcon }) => {
          const isActiveTab = selected && effectiveTab === id;
          return (
            <button
              key={id}
              id={`Scene3DHierarchy__${id}TabTrigger-${WORLD_NODE_ID}`}
              type="button"
              title={label}
              onClick={(e) => {
                e.stopPropagation();
                onSelectTab(id);
              }}
              className={`w-5 h-5 flex items-center justify-center shrink-0 ${
                isActiveTab
                  ? "text-primary"
                  : "text-secondary-foreground/50 hover:text-secondary-foreground"
              }`}>
              <TabIcon className="w-3.5 h-3.5" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Row({
  node,
  depth,
  nodes,
  selectedNodeId,
  onSelect,
  onToggleVisible,
  onDrop,
  draggedId,
  setDraggedId,
  onRename,
  activeTab,
  onSelectTab,
}) {
  const [expanded, setExpanded] = useState(true);
  const [dropIndicator, setDropIndicator] = useState(null); // 'before' | 'after' | 'inside'
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(node.name);
  const renameCommittedRef = useRef(false);
  const rowRef = useRef(null);
  const kids = childrenOf(nodes, node._id);
  const Icon = NODE_ICON[node.type] || FiBox;
  const isSelected = node._id === selectedNodeId;
  const containsSelected =
    selectedNodeId != null &&
    !isSelected &&
    isDescendantOf(nodes, selectedNodeId, node._id);

  // Reveals a selection made elsewhere (clicking an object in the 3D
  // viewport, most notably) rather than in this panel — expand every
  // collapsed ancestor on the way to it, then scroll its own row into view.
  // One-way only: never collapses an ancestor just because the selection
  // moved on, matching how an outliner staying manually organized is
  // normally expected to behave.
  useEffect(() => {
    if (containsSelected) setExpanded(true);
  }, [containsSelected]);

  useEffect(() => {
    if (isSelected) rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [isSelected]);

  const startRenaming = (e) => {
    e.stopPropagation();
    renameCommittedRef.current = false;
    setDraftName(node.name);
    setEditingName(true);
  };

  // Enter commits, Escape/blur cancels — `renameCommittedRef` lets the
  // Enter handler close the input without the blur it triggers re-firing
  // a cancel right behind it.
  const finishRenaming = (commit) => {
    renameCommittedRef.current = true;
    if (commit) {
      const trimmed = draftName.trim();
      if (trimmed && trimmed !== node.name) onRename(node._id, trimmed);
    }
    setEditingName(false);
  };

  const rejectsDrop =
    draggedId != null &&
    (draggedId === node._id || isDescendantOf(nodes, node._id, draggedId));

  return (
    <div>
      <div
        ref={rowRef}
        id={`Scene3DHierarchy__row-${node._id}`}
        draggable={!editingName}
        onDragStart={(e) => {
          e.dataTransfer.setData(DRAG_MIME, node._id);
          e.dataTransfer.effectAllowed = "move";
          setDraggedId(node._id);
        }}
        onDragEnd={() => {
          setDraggedId(null);
          setDropIndicator(null);
        }}
        onDragOver={(e) => {
          if (rejectsDrop) return;
          e.preventDefault();
          e.stopPropagation(); // otherwise this bubbles to the panel's own
          // root-level onDragOver, which would show/arm a "drop at root" target
          // underneath whatever row is actually being hovered.
          e.dataTransfer.dropEffect = "move";
          setDropIndicator(
            dropPositionFor(e, e.currentTarget.getBoundingClientRect()),
          );
        }}
        onDragLeave={() => setDropIndicator(null)}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation(); // otherwise this bubbles to the panel's own
          // root-level onDrop, which would immediately re-parent the node back
          // to root right after this handler correctly reparents/reorders it —
          // the exact bug that made drops appear to silently fail.
          const draggedNodeId = e.dataTransfer.getData(DRAG_MIME);
          const position = dropIndicator;
          setDropIndicator(null);
          setDraggedId(null);
          if (!draggedNodeId || rejectsDrop) return;
          onDrop(draggedNodeId, node, position);
        }}
        onClick={() => onSelect(node._id)}
        className={`flex items-center gap-1 pr-2 py-1 cursor-pointer text-xs select-none relative
          ${isSelected ? "bg-secondary/60 text-primary" : "hover:bg-secondary/30"}
          ${dropIndicator === "inside" ? "outline outline-1 outline-primary" : ""}`}
        style={{ paddingLeft: depth * 16 + 4 }}>
        {dropIndicator === "before" && (
          <div className="absolute left-0 right-0 top-0 h-0.5 bg-primary" />
        )}
        {dropIndicator === "after" && (
          <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-primary" />
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className={`w-4 h-4 flex items-center justify-center shrink-0 ${kids.length ? "" : "invisible"}`}>
          {expanded ? (
            <FiChevronDown className="w-3 h-3" />
          ) : (
            <FiChevronRight className="w-3 h-3" />
          )}
        </button>
        <Icon className="w-3.5 h-3.5 shrink-0 opacity-70" />
        {editingName ? (
          <input
            id={`Scene3DHierarchy__nameInput-${node._id}`}
            autoFocus
            onFocus={(e) => e.target.select()}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                finishRenaming(true);
              } else if (e.key === "Escape") {
                e.preventDefault();
                finishRenaming(false);
              }
            }}
            onBlur={() => {
              if (!renameCommittedRef.current) finishRenaming(false);
            }}
            className="flex-1 min-w-0 text-xs bg-secondary/50 border border-primary/50 rounded-sm px-1 outline-none"
          />
        ) : (
          <span className="flex-1 truncate" onDoubleClick={startRenaming}>
            {node.name}
          </span>
        )}
        <div className="flex gap-1">
          <button
            type="button"
            title={node.visible === false ? "Show" : "Hide"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisible(node._id);
            }}
            className="w-5 h-5 flex items-center justify-center shrink-0 text-secondary-foreground/50 hover:text-secondary-foreground">
            {node.visible === false ? (
              <FiEyeOff className="w-3.5 h-3.5" />
            ) : (
              <FiEye className="w-3.5 h-3.5" />
            )}
          </button>
          {(() => {
            const tabById = new Map(
              tabsForNodeType(node.type).map((t) => [t.id, t]),
            );
            // Transform is always the first column (every type has it);
            // the rest render in EXTRA_TAB_COLUMNS' fixed order, an empty
            // same-size placeholder standing in for a tab this node type
            // doesn't have, so e.g. "AI" lands in the same column whether
            // this row is a mesh, a light, or a camera.
            return [TRANSFORM_TAB.id, ...EXTRA_TAB_COLUMNS].map((id) => {
              const tab = tabById.get(id);
              if (!tab) {
                return (
                  <span
                    key={id}
                    aria-hidden="true"
                    className="w-5 h-5 shrink-0"
                  />
                );
              }
              const TabIcon = tab.icon;
              const isActiveTab = isSelected && activeTab === id;
              return (
                <button
                  key={id}
                  id={`Scene3DHierarchy__${id}TabTrigger-${node._id}`}
                  type="button"
                  title={tab.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectTab(node._id, id);
                  }}
                  className={`relative w-5 h-5 flex items-center justify-center shrink-0 ${
                    isActiveTab
                      ? "text-primary"
                      : "text-secondary-foreground/50 hover:text-secondary-foreground"
                  }`}>
                  <TabIcon className="w-3.5 h-3.5" />
                  {id === "ai" && hasAiContent(node) && (
                    <span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-primary" />
                  )}
                </button>
              );
            });
          })()}
        </div>
      </div>
      {expanded &&
        kids.map((child) => (
          <Row
            key={child._id}
            node={child}
            depth={depth + 1}
            nodes={nodes}
            selectedNodeId={selectedNodeId}
            onSelect={onSelect}
            onToggleVisible={onToggleVisible}
            onDrop={onDrop}
            draggedId={draggedId}
            setDraggedId={setDraggedId}
            onRename={onRename}
            activeTab={activeTab}
            onSelectTab={onSelectTab}
          />
        ))}
    </div>
  );
}

export default function HierarchyPanel({
  nodes,
  selectedNodeId,
  onSelect,
  onToggleVisible,
  onReorder,
  onRename,
  activeTab,
  onSelectTab,
  onAddMesh,
  onImportModel,
  importingModel,
  onAddNull,
  onAddLight,
  onAddCamera,
}) {
  const [draggedId, setDraggedId] = useState(null);
  const roots = childrenOf(nodes, null);

  const handleDrop = (draggedNodeId, targetNode, position) => {
    if (position === "inside") {
      onReorder(
        draggedNodeId,
        targetNode._id,
        childrenOf(nodes, targetNode._id).length,
      );
      return;
    }
    const siblings = childrenOf(nodes, targetNode.parent);
    const targetIndex = siblings.findIndex((n) => n._id === targetNode._id);
    onReorder(
      draggedNodeId,
      targetNode.parent,
      position === "after" ? targetIndex + 1 : targetIndex,
    );
  };

  return (
    <div id="Scene3DHierarchyPanel" className="flex flex-col h-full">
      <AddNodeToolbar
        onAddMesh={onAddMesh}
        onImportModel={onImportModel}
        importingModel={importingModel}
        onAddNull={onAddNull}
        onAddLight={onAddLight}
        onAddCamera={onAddCamera}
      />
      <div
        className="flex flex-col overflow-y-auto flex-1 min-h-0 py-1"
        onDragOver={(e) => {
          if (!draggedId) return;
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const draggedNodeId = e.dataTransfer.getData(DRAG_MIME);
          if (draggedNodeId) onReorder(draggedNodeId, null, roots.length);
          setDraggedId(null);
        }}>
        <WorldRow
          selected={selectedNodeId === WORLD_NODE_ID}
          activeTab={activeTab}
          onSelect={() => onSelect(WORLD_NODE_ID)}
          onSelectTab={(tabId) => onSelectTab(WORLD_NODE_ID, tabId)}
        />
        {roots.map((node) => (
          <Row
            key={node._id}
            node={node}
            depth={0}
            nodes={nodes}
            selectedNodeId={selectedNodeId}
            onSelect={onSelect}
            onToggleVisible={onToggleVisible}
            onDrop={handleDrop}
            draggedId={draggedId}
            setDraggedId={setDraggedId}
            onRename={onRename}
            activeTab={activeTab}
            onSelectTab={onSelectTab}
          />
        ))}
      </div>
    </div>
  );
}
